import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { Close, Receipt, Refresh, Download } from "@mui/icons-material";
import axios from "axios";
import { BASE_URL } from "../../../../services/configUrls";

const ViewPaymentDetailsModal = ({
  open,
  onClose,
  bookslotId,
  instituteId,
}) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [retryingPaymentId, setRetryingPaymentId] = useState(null);

  useEffect(() => {
    if (open && bookslotId && instituteId) {
      fetchPaymentDetails();
    }
  }, [open, bookslotId, instituteId]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${BASE_URL}/event/payments/${bookslotId}/institute/${instituteId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      setPayments(response.data.data.payments || []);
    } catch (err) {
      console.error("Error fetching payment details:", err);
      setError("Failed to fetch payment details");
    } finally {
      setLoading(false);
    }
  };

  const formatLocalDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (err) {
      return dateTimeStr;
    }
  };

  const handleViewInvoice = async (paymentId) => {
    try {
      setInvoiceLoading(true);
      setSelectedPaymentId(paymentId);
      setInvoicePreviewOpen(true);

      const response = await axios.post(
        `${BASE_URL}/event/generate-invoice/${paymentId}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      if (response.data && response.data.pdf_base64) {
        const base64Data = response.data.pdf_base64;
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);

        setInvoicePdfUrl(url);
      }
    } catch (err) {
      console.error("Error loading invoice:", err);
      alert("Failed to load invoice. Please try again.");
      setInvoicePreviewOpen(false);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleDownloadInvoice = () => {
    if (invoicePdfUrl && selectedPaymentId) {
      const link = document.createElement("a");
      link.href = invoicePdfUrl;
      link.download = `invoice_${selectedPaymentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleCloseInvoicePreview = () => {
    setInvoicePreviewOpen(false);
    if (invoicePdfUrl) {
      window.URL.revokeObjectURL(invoicePdfUrl);
      setInvoicePdfUrl(null);
    }
    setSelectedPaymentId(null);
  };

  // Separate function to open Razorpay modal
  // Separate function to open Razorpay modal
const openRazorpayModal = (retryData) => {
  const options = {
    key: retryData.razorpay_key_id,
    amount: parseFloat(retryData.amount) * 100, // Convert to paise
    currency: retryData.currency || "INR",
    name: "EduSkills Foundation",
    description: `EDP Faculty Nomination - ${retryData.faculty_count} Faculty`,
    order_id: retryData.order_id,
    handler: async function (razorpayResponse) {
      // Payment successful - verify it
      try {
        // Send as query parameters instead of body
        const verifyResponse = await axios.post(
          `${BASE_URL}/event/verify-payment/${retryData.payment_id}?razorpay_payment_id=${razorpayResponse.razorpay_payment_id}&razorpay_order_id=${razorpayResponse.razorpay_order_id}&razorpay_signature=${razorpayResponse.razorpay_signature}`,
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          },
        );

        setRetryingPaymentId(null);

        if (verifyResponse.data.status === "success") {
          alert("Payment successful! Refreshing payment details...");

          // Refresh payment list
          setTimeout(() => {
            fetchPaymentDetails();
          }, 1000);
        } else {
          alert("Payment verification failed. Please contact support.");

          setTimeout(() => {
            fetchPaymentDetails();
          }, 1000);
        }
      } catch (verifyError) {
        console.error("Payment verification failed:", verifyError);
        setRetryingPaymentId(null);
        alert("Payment verification failed. Please contact support.");

        // Still refresh to show updated status
        setTimeout(() => {
          fetchPaymentDetails();
        }, 1000);
      }
    },
    prefill: {
      name: retryData.institute_name || "",
    },
    theme: {
      color: "#1976d2",
    },
    modal: {
      ondismiss: function () {
        console.log("Razorpay modal dismissed by user");
        setRetryingPaymentId(null);
      },
      escape: true,
      backdropclose: false,
    },
  };

  try {
    const rzp = new window.Razorpay(options);

    // Add event listeners for payment failure
    rzp.on("payment.failed", function (response) {
      console.error("Payment failed:", response.error);
      setRetryingPaymentId(null);
      alert(
        `Payment failed: ${response.error.description || "Please try again."}`
      );

      // Refresh to show updated status
      setTimeout(() => {
        fetchPaymentDetails();
      }, 1000);
    });

    console.log("Opening Razorpay modal...");
    rzp.open();
  } catch (error) {
    console.error("Error opening Razorpay:", error);
    alert("Failed to open payment gateway. Please try again.");
    setRetryingPaymentId(null);
  }
};

  const handleRetryPayment = async (paymentId) => {
    try {
      setRetryingPaymentId(paymentId);
      console.log("Starting retry for payment:", paymentId);

      // Call retry payment API
      const response = await axios.post(
        `${BASE_URL}/event/retry-new/${paymentId}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        },
      );

      console.log("Full Retry API response:", response);
      console.log("Response data:", response.data);

      // Handle response - check if data is wrapped or direct
      let retryData;

      // If response has status and data fields (wrapped response)
      if (response.data.status && response.data.data) {
        retryData = response.data.data;
      }
      // If response data directly contains payment fields (direct response from FastAPI)
      else if (response.data.payment_id && response.data.razorpay_key_id) {
        retryData = response.data;
      }
      // Fallback
      else {
        retryData = response.data;
      }

      console.log("Parsed retry data:", retryData);

      // Check if this is a PaymentInitiationResponse (needs Razorpay) or completion response
      if (retryData.razorpay_key_id && retryData.order_id) {
        // Payment needs to be completed via Razorpay
        console.log("Payment needs Razorpay - opening modal");

        // Check if Razorpay script is already loaded
        if (window.Razorpay) {
          // Script already loaded, open modal directly
          console.log("Razorpay already loaded, opening modal...");
          openRazorpayModal(retryData);
        } else {
          // Load Razorpay script
          console.log("Loading Razorpay script...");
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.async = true;
          document.body.appendChild(script);

          script.onload = () => {
            console.log("Razorpay script loaded successfully");
            openRazorpayModal(retryData);
          };

          script.onerror = () => {
            console.error("Failed to load Razorpay script");
            alert("Failed to load payment gateway. Please try again.");
            setRetryingPaymentId(null);
          };
        }
      } else if (
        retryData.payment_status === "completed" ||
        retryData.status === "completed"
      ) {
        // Payment was already completed by backend
        console.log("Payment already completed");
        setRetryingPaymentId(null);
        alert("Payment was already completed successfully! Refreshing...");

        // Refresh payment list
        setTimeout(() => {
          fetchPaymentDetails();
        }, 1000);
      } else {
        // Unknown response format
        console.log("Unknown response format:", retryData);
        setRetryingPaymentId(null);
        alert(retryData.message || "Payment status updated. Please refresh.");

        setTimeout(() => {
          fetchPaymentDetails();
        }, 1000);
      }
    } catch (err) {
      console.error("Error retrying payment:", err);
      console.error("Error details:", err.response?.data);
      alert(
        err.response?.data?.detail ||
          "Failed to retry payment. Please try again.",
      );
      setRetryingPaymentId(null);
    }
  };

  const getStatusChip = (status) => {
  const statusConfig = {
    completed: { color: "#4caf50", label: "Completed" },
    pending: { color: "#ff9800", label: "Pending" },
    failed: { color: "#f44336", label: "Failed" },
    initiated: { color: "#2196f3", label: "Initiated" },
  };

  // Normalize status - treat faculty_added and captured as completed
  const normalizedStatus = status.toLowerCase();
  const mappedStatus = 
    normalizedStatus === "faculty_added" || normalizedStatus === "captured"
      ? "completed"
      : normalizedStatus;

  const config = statusConfig[mappedStatus] || {
    color: "#9e9e9e",
    label: status,
  };

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.color,
        color: "white",
        fontWeight: 500,
        fontSize: "0.75rem",
      }}
    />
  );
};


  const handleModalClose = () => {
    setPayments([]);
    setError(null);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
            pb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Payment Details
          </Typography>
          <IconButton
            onClick={handleModalClose}
            size="small"
            sx={{
              color: "#666",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <Typography color="error">{error}</Typography>
            </Box>
          ) : payments.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <Typography color="text.secondary">
                No payment records found
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                borderRadius: 2,
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      Date & Time
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      Faculty Added
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment, index) => (
                    <TableRow
                      key={payment.payment_id || index}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {formatLocalDateTime(payment.date_time)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {payment.faculty_added}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ fontSize: "0.875rem" }}
                        >
                          {payment.currency} {payment.amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(payment.status)}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {payment.status.toLowerCase() === "initiated" ||
                          payment.status.toLowerCase() === "pending" ||
                          payment.status.toLowerCase() === "failed" ? (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Refresh />}
                              onClick={() =>
                                handleRetryPayment(payment.payment_id)
                              }
                              disabled={
                                retryingPaymentId === payment.payment_id
                              }
                              sx={{
                                textTransform: "none",
                                color: "#ff9800",
                                borderColor: "#ff9800",
                                fontSize: "0.8rem",
                                "&:hover": {
                                  backgroundColor: "#fff3e0",
                                  borderColor: "#ff9800",
                                },
                              }}
                            >
                              {retryingPaymentId === payment.payment_id
                                ? "Processing..."
                                : "Retry"}
                            </Button>
                          ) : payment.status.toLowerCase() === "completed" || payment.status.toLowerCase() === "faculty_added" || payment.status.toLowerCase() === "captured" ? (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<Receipt />}
                              onClick={() =>
                                handleViewInvoice(payment.payment_id)
                              }
                              sx={{
                                textTransform: "none",
                                color: "#1976d2",
                                borderColor: "#1976d2",
                                fontSize: "0.8rem",
                                "&:hover": {
                                  backgroundColor: "#e3f2fd",
                                  borderColor: "#1976d2",
                                },
                              }}
                            >
                              View Invoice
                            </Button>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid #e0e0e0",
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={handleModalClose}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Preview Modal */}
      <Dialog
        open={invoicePreviewOpen}
        onClose={handleCloseInvoicePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Invoice Preview
          </Typography>
          <IconButton
            onClick={handleCloseInvoicePreview}
            size="small"
            sx={{
              color: "#666",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f5f5f5",
          }}
        >
          {invoiceLoading ? (
            <CircularProgress />
          ) : invoicePdfUrl ? (
            <iframe
              src={invoicePdfUrl}
              title="Invoice Preview"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          ) : (
            <Typography color="text.secondary">
              Failed to load invoice
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: "1px solid #e0e0e0",
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={handleCloseInvoicePreview}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
          <Button
            onClick={handleDownloadInvoice}
            variant="contained"
            startIcon={<Download />}
            disabled={!invoicePdfUrl}
            sx={{ textTransform: "none" }}
          >
            Download Invoice
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ViewPaymentDetailsModal;
